import { Link } from "@tanstack/react-router";
import { lazy, Suspense, useMemo } from "react";
import { useInView } from "@/hooks/use-in-view";
import { Layout } from "@/components/Layout";
import { SEOHead } from "@/components/SEOHead";
import { NumberRow } from "@/components/NumberPill";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { GameLogo } from "@/components/GameLogo";
import { RouteNotFound } from "@/components/RouteNotFound";
import { CountdownTicker } from "@/components/CountdownTicker";
import { useGameBySlug, useResults, useStateBySlug } from "@/hooks/use-query-hooks";
import { formatJackpot, formatDate, formatDateTime } from "@/lib/format";
import { FIXED_PRIZE_GAMES } from "@/lib/game-logos";
import { gameIntro, buildGameFaqs, type GameCopyInput } from "@/lib/page-copy";
import { RelatedSections } from "@/components/RelatedSections";
import { gamePaths } from "@/lib/game-routing";
import { relatedGamesForGame, relatedStatesForGame } from "@/lib/related-links";
import { Sparkles, CheckCircle, Calculator, BarChart3, Loader2, ExternalLink, BookOpen, TrendingUp } from "lucide-react";

const LazyGameFaqSection = lazy(() => import("./GameFaqSection"));
const LazyGameRelatedLinks = lazy(() => import("./GameRelatedLinks"));

const KNOWN_URLS: Record<string, string> = {
  "powerball": "https://www.powerball.com",
  "mega-millions": "https://www.megamillions.com",
  "lucky-for-life": "https://www.luckyforlife.us",
  "lotto-america": "https://www.lottoamerica.com",
  "cash4life": "https://www.cash4life.com",
};

/** Untyped Link wrapper for string hrefs shared by both game URL shapes. */
const A = Link as unknown as React.ComponentType<Record<string, unknown>>;

export function GamePageView({ gameSlug, stateSlug }: { gameSlug: string; stateSlug?: string }) {
  const paths = gamePaths(gameSlug, stateSlug);
  const { data: state } = useStateBySlug(stateSlug);
  const { data: game, isLoading } = useGameBySlug(gameSlug, state?.id);
  const { data: results } = useResults(game?.id, 25);

  const trends = useMemo(() => {
    if (!results || results.length < 1) return null;
    const freq: Record<number, number> = {};
    results.forEach((r) => {
      (r.winning_numbers || []).forEach((n) => {
        freq[n] = (freq[n] || 0) + 1;
      });
    });
    const sorted = Object.entries(freq).sort(([, a], [, b]) => b - a);
    return {
      hot: sorted.slice(0, 5).map(([n]) => parseInt(n)),
      cold: sorted.slice(-5).reverse().map(([n]) => parseInt(n)),
    };
  }, [results]);

  if (isLoading) {
    return (
      <Layout>
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      </Layout>
    );
  }
  if (!game) {
    return (
      <Layout>
        <RouteNotFound gameSlug={gameSlug} />
      </Layout>
    );
  }

  const jp = (game as any).jackpot_snapshots as { jackpot_amount: number; cash_value: number | null; next_draw_datetime: string } | null;
  const gameState = (game as any).states;
  const latestResult = results?.[0];
  const previousResult = results?.[1];
  const playNowUrl = KNOWN_URLS[game.slug] || gameState?.official_lottery_url || null;
  const isHowTo = game.slug === "powerball" || game.slug === "mega-millions";
  const copyInput: GameCopyInput = {
    name: game.name,
    slug: game.slug,
    stateName: gameState?.name,
    stateSlug: stateSlug,
    drawSchedule: game.draw_schedule,
    ticketPrice: game.ticket_price,
    mainCount: game.number_count_main,
    mainRange: game.number_range_main,
    bonusCount: game.number_count_bonus,
    bonusRange: game.number_range_bonus,
    oddsInfo: game.odds_info,
    isMultistate: game.is_multistate,
  };

  return (
    <Layout>
      <SEOHead
        title={`${game.name} — Jackpot, Results & Numbers`}
        description={`${game.name} current jackpot: ${jp?.jackpot_amount ? formatJackpot(jp.jackpot_amount) : "Check latest"}. View winning numbers, results history, and use free lottery tools.`}
      />
      <div className="container py-6 md:py-10">
        <Breadcrumb className="mb-4">
          <BreadcrumbList>
            <BreadcrumbItem><BreadcrumbLink asChild><Link to="/">Home</Link></BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            {stateSlug && gameState ? (
              <>
                <BreadcrumbItem><BreadcrumbLink asChild><Link to="/states">States</Link></BreadcrumbLink></BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/states/$stateSlug" params={{ stateSlug }}>{gameState.name}</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
              </>
            ) : (
              <>
                <BreadcrumbItem><BreadcrumbLink asChild><Link to="/">Games</Link></BreadcrumbLink></BreadcrumbItem>
                <BreadcrumbSeparator />
              </>
            )}
            <BreadcrumbItem><BreadcrumbPage>{game.name}</BreadcrumbPage></BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <Card className="mb-8">
          <CardContent className="pt-8 pb-6">
            <div className="flex flex-col items-center text-center gap-4">
              <GameLogo gameSlug={game.slug} gameName={game.name} logoUrl={game.logo_url} size="hero" />
              <div className="flex flex-col sm:flex-row items-center gap-2">
                <h1 className="font-sans text-[28px] md:text-[30px] font-bold tracking-tight">{game.name} Jackpot, Results &amp; Numbers</h1>
                <div className="flex flex-wrap justify-center gap-2">
                  {game.is_multistate && <Badge variant="secondary">Multi-State</Badge>}
                  {gameState && <Badge variant="outline">{gameState.abbreviation}</Badge>}
                </div>
              </div>

              {jp && jp.jackpot_amount > 0 ? (
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Estimated Jackpot</p>
                  <p className="jackpot-amount text-4xl md:text-5xl text-primary">{formatJackpot(jp.jackpot_amount)}</p>
                  {jp.cash_value && <p className="text-muted-foreground mt-1">Est. Cash Value: {formatJackpot(jp.cash_value)}</p>}
                </div>
              ) : jp && jp.cash_value && jp.cash_value > 0 ? (
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Top Prize</p>
                  <p className="jackpot-amount text-4xl md:text-5xl text-primary">{formatJackpot(jp.cash_value)}</p>
                </div>
              ) : FIXED_PRIZE_GAMES[game.slug] ? (
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">{FIXED_PRIZE_GAMES[game.slug].label}</p>
                  <p className="jackpot-amount text-4xl md:text-5xl text-primary">{FIXED_PRIZE_GAMES[game.slug].amount}</p>
                </div>
              ) : null}

              {playNowUrl && (
                <Button size="lg" className="text-base px-8" asChild>
                  <a href={playNowUrl} target="_blank" rel="noopener noreferrer">
                    Play Now <ExternalLink className="h-4 w-4 ml-1" />
                  </a>
                </Button>
              )}

              {jp?.next_draw_datetime && (
                <div>
                  <CountdownTicker targetDate={jp.next_draw_datetime} variant="card" />
                  <p className="text-xs text-muted-foreground mt-2">{formatDateTime(jp.next_draw_datetime)}</p>
                </div>
              )}

              {game.draw_schedule && (
                <p className="text-sm text-muted-foreground">Schedule: {game.draw_schedule}</p>
              )}
            </div>
          </CardContent>
        </Card>

        <section className="mb-8">
          <h2 className="font-sans text-[20px] font-bold mb-3 tracking-tight">Latest winning numbers</h2>
          <p className="mb-4 max-w-3xl text-sm text-muted-foreground">{gameIntro(copyInput)}</p>
          <div className="grid gap-4 sm:grid-cols-2">
            {latestResult && (
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base">Latest Winning Numbers</CardTitle></CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-2">{formatDate(latestResult.draw_date)}</p>
                  <NumberRow numbers={latestResult.winning_numbers || []} bonusNumbers={latestResult.bonus_numbers || []} multiplier={latestResult.multiplier} size="lg" align="left" />
                </CardContent>
              </Card>
            )}
            {previousResult && (
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base">Previous Draw</CardTitle></CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-2">{formatDate(previousResult.draw_date)}</p>
                  <NumberRow numbers={previousResult.winning_numbers || []} bonusNumbers={previousResult.bonus_numbers || []} multiplier={previousResult.multiplier} size="lg" align="left" />
                </CardContent>
              </Card>
            )}
          </div>
        </section>

        {results && results.length > 0 && (
          <section className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-sans text-[20px] font-bold tracking-tight">Results History</h2>
              <A to={paths.history} className="text-sm text-primary hover:underline">View all results →</A>
            </div>
            <Card className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Winning Numbers</TableHead>
                    {game.has_multiplier && <TableHead className="hidden sm:table-cell">Multiplier</TableHead>}
                    <TableHead className="hidden sm:table-cell text-right">Draw details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {results.slice(0, 10).map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="text-sm whitespace-nowrap">
                        <A to={paths.result(r.draw_date)} className="text-primary hover:underline">
                          {formatDate(r.draw_date)}
                        </A>
                      </TableCell>
                      <TableCell>
                        <NumberRow numbers={r.winning_numbers || []} bonusNumbers={r.bonus_numbers || []} size="sm" nowrap align="left" />
                      </TableCell>
                      {game.has_multiplier && <TableCell className="hidden sm:table-cell">{r.multiplier ? `x${r.multiplier}` : "—"}</TableCell>}
                      <TableCell className="hidden sm:table-cell text-right">
                        <A to={paths.result(r.draw_date)} className="text-xs text-primary hover:underline">
                          View draw →
                        </A>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
            <div className="flex flex-wrap justify-center gap-3 mt-4">
              <Button variant="outline" asChild>
                <A to={paths.history}>View Full History (25+ draws)</A>
              </Button>
              {stateSlug && gameState && (
                <Button variant="outline" asChild>
                  <Link to="/states/$stateSlug" params={{ stateSlug }}>All {gameState.name} lottery games →</Link>
                </Button>
              )}
            </div>
          </section>
        )}

        {trends && (
          <section className="mb-8">
            <h2 className="font-sans text-[20px] font-bold mb-3 tracking-tight">Number Trends</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <Card className="border-0 shadow-none">
                <CardHeader className="pb-2"><CardTitle className="text-base text-foreground">🔥 Hot Numbers</CardTitle></CardHeader>
                <CardContent>
                  <div className="flex gap-2">
                    {trends.hot.map((n) => (
                      <span key={n} className="inline-flex h-10 w-10 items-center justify-center rounded-full border-0 font-bold bg-accent text-accent-foreground">{n}</span>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">Most frequently drawn in last 25 draws</p>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-none">
                <CardHeader className="pb-2"><CardTitle className="text-base text-foreground">❄️ Cold Numbers</CardTitle></CardHeader>
                <CardContent>
                  <div className="flex gap-2">
                    {trends.cold.map((n) => (
                      <span key={n} className="inline-flex h-10 w-10 items-center justify-center rounded-full border-0 font-bold bg-lottery-blue text-lottery-blue-foreground">{n}</span>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">Least frequently drawn in last 25 draws</p>
                </CardContent>
              </Card>

            </div>
          </section>
        )}

        {(game.description || game.odds_info) && (
          <section className="mb-8">
            <h2 className="font-sans text-[20px] font-bold mb-3 tracking-tight">How to Play {game.name}</h2>
            {game.description && <p className="text-muted-foreground mb-2">{game.description}</p>}
            {game.odds_info && <p className="text-sm text-muted-foreground">{game.odds_info}</p>}
            {game.ticket_price ? <p className="text-sm text-muted-foreground mt-1">Ticket price: ${Number(game.ticket_price).toFixed(2)}</p> : null}
            {isHowTo && (
              <Button variant="outline" className="mt-3" asChild>
                {game.slug === "powerball" ? (
                  <Link to="/powerball/how-to-play"><BookOpen className="h-4 w-4 mr-1" /> Full Rules, Prizes &amp; Odds Guide →</Link>
                ) : (
                  <Link to="/mega-millions/how-to-play"><BookOpen className="h-4 w-4 mr-1" /> Full Rules, Prizes &amp; Odds Guide →</Link>
                )}
              </Button>
            )}
          </section>
        )}

        <section>
          <h2 className="font-sans text-[20px] font-bold mb-3 tracking-tight">Tools</h2>
          <div className="grid gap-3 grid-cols-2 lg:grid-cols-5">
            {isHowTo && (
              <Button variant="outline" className="h-auto py-3 flex flex-col items-center gap-1" asChild>
                <Link to={game.slug === "powerball" ? "/powerball/how-to-play" : "/mega-millions/how-to-play"}>
                  <BookOpen className="h-5 w-5 text-primary" />
                  <span className="text-xs">How to Play</span>
                </Link>
              </Button>
            )}
            <Button variant="outline" className="h-auto py-3 flex flex-col items-center gap-1" asChild>
              <Link to="/tools/number-generator" search={{ game: game.slug }}>
                <Sparkles className="h-5 w-5 text-primary" />
                <span className="text-xs">Generate Numbers</span>
              </Link>
            </Button>
            <Button variant="outline" className="h-auto py-3 flex flex-col items-center gap-1" asChild>
              <Link to="/tools/results-checker" search={{ game: game.slug }}>
                <CheckCircle className="h-5 w-5 text-primary" />
                <span className="text-xs">Check Results</span>
              </Link>
            </Button>
            <Button variant="outline" className="h-auto py-3 flex flex-col items-center gap-1" asChild>
              <Link to="/tools/tax-calculator">
                <Calculator className="h-5 w-5 text-primary" />
                <span className="text-xs">Tax Calculator</span>
              </Link>
            </Button>
            <Button variant="outline" className="h-auto py-3 flex flex-col items-center gap-1" asChild>
              <Link to="/tools/frequency-analyzer" search={{ game: game.slug }}>
                <BarChart3 className="h-5 w-5 text-primary" />
                <span className="text-xs">Frequency Analyzer</span>
              </Link>
            </Button>
            {(game.slug === "powerball" || game.slug === "mega-millions") && (
              <Button variant="outline" className="h-auto py-3 flex flex-col items-center gap-1" asChild>
                <Link to={game.slug === "powerball" ? "/powerball/jackpot-analysis" : "/mega-millions/jackpot-analysis"}>
                  <TrendingUp className="h-5 w-5 text-primary" />
                  <span className="text-xs">Payout Analysis</span>
                </Link>
              </Button>
            )}
          </div>
        </section>

        <LazyDeferredSections copy={copyInput} />

        <RelatedSections
          gameSlug={game.slug}
          stateSlug={stateSlug}
          games={relatedGamesForGame(game.slug, stateSlug)}
          states={relatedStatesForGame(game.slug, game.is_multistate, stateSlug)}
          gamesHeading={`Lottery games related to ${game.name}`}
          statesHeading={
            gameState ? `Lottery results in states near ${gameState.name}` : "Lottery results by state"
          }
        />
      </div>
    </Layout>
  );
}

function LazyDeferredSections({ copy }: { copy: GameCopyInput }) {
  const gameSlug = copy.slug;
  const gameName = copy.name;
  const stateSlug = copy.stateSlug;
  const stateName = copy.stateName;
  const { ref, inView } = useInView<HTMLDivElement>("400px");
  const relatedLinks: Array<{ to: string; params?: Record<string, string>; label: string; targetPath: string }> = [
    {
      to: stateSlug ? "/$stateSlug/$gameSlug/history" : "/games/$gameSlug/history",
      params: stateSlug ? { stateSlug, gameSlug } : { gameSlug },
      label: `${gameName} full results history`,
      targetPath: gamePaths(gameSlug, stateSlug).history,
    },
    ...(stateSlug && stateName
      ? [{
          to: "/states/$stateSlug",
          params: { stateSlug },
          label: `All ${stateName} lottery games`,
          targetPath: `/states/${stateSlug}`,
        }]
      : []),
    ...(gameSlug !== "powerball"
      ? [{
          to: "/games/$gameSlug",
          params: { gameSlug: "powerball" },
          label: "Powerball results",
          targetPath: "/games/powerball",
        }]
      : []),
    ...(gameSlug !== "mega-millions"
      ? [{
          to: "/games/$gameSlug",
          params: { gameSlug: "mega-millions" },
          label: "Mega Millions results",
          targetPath: "/games/mega-millions",
        }]
      : []),
    { to: "/results", label: "Today's lottery results across all states", targetPath: "/results" },
  ];

  return (
    <div ref={ref} className="min-h-[200px]">
      {inView && (
        <Suspense fallback={<div className="h-40" aria-hidden />}>
          <LazyGameFaqSection
            faqs={buildGameFaqs(copy)}
            gameSlug={gameSlug}
            stateSlug={stateSlug}
            heading={stateName ? `${stateName} ${gameName} — FAQ` : `${gameName} — FAQ`}
          />
          <LazyGameRelatedLinks links={relatedLinks} gameSlug={gameSlug} stateSlug={stateSlug} />
        </Suspense>
      )}
    </div>
  );
}
